import React, { useEffect, useRef, useState } from 'react';
import ModuleTopExtras from '../ModuleTopExtras';
import { useLanguage } from '../../i18n/LanguageContext';
import { API_ENDPOINTS, apiRequest } from '../../config/api';
import { renderRichContent, richContentToPlainText } from '../../utils/htmlContent';
import { downloadText } from '../../utils/download';
import { AdProcessingBanner } from '../ads/Ads';
import './Modules.css';

// Max limits
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

// Progress curve: start at 8% and ease asymptotically toward 90% so the bar
// keeps moving for the whole (unbounded) request but never completes until the
// server actually responds. ~20s time constant -> ~24% at 5s, ~70% at 30s.
const PROGRESS_HEAD_START = 8;
const PROGRESS_CEILING = 90;
const PROGRESS_TIME_CONSTANT_MS = 20000;

const statusForProgress = (pct: number): string => {
  if (pct < 35) return 'Reading the pages of your PDF with AI…';
  if (pct < 65) return 'Transcribing and organising the content…';
  if (pct < 85) return 'Writing your structured study notes…';
  return 'Still working — long documents take a little longer…';
};

const NotesModule: React.FC = () => {
  const { t } = useLanguage();
  const [currentView, setCurrentView] = useState<'input' | 'result'>('input');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [generatedHtml, setGeneratedHtml] = useState('');
  const [resultMeta, setResultMeta] = useState<{ pages?: number; totalPages?: number; chunks?: number; truncated?: boolean }>({});

  const fileInputRef = useRef<HTMLInputElement>(null);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressStartRef = useRef<number>(0);

  const stopProgress = () => {
    if (progressTimerRef.current !== null) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  // Advance the bar on an elapsed-time easing curve until the response lands.
  const startProgress = () => {
    stopProgress();
    progressStartRef.current = Date.now();
    setProgress(PROGRESS_HEAD_START);
    setStatusMessage(statusForProgress(PROGRESS_HEAD_START));
    progressTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - progressStartRef.current;
      const pct = PROGRESS_HEAD_START +
        (PROGRESS_CEILING - PROGRESS_HEAD_START) *
        (1 - Math.exp(-elapsed / PROGRESS_TIME_CONSTANT_MS));
      setProgress(pct);
      setStatusMessage(statusForProgress(pct));
    }, 200);
  };

  useEffect(() => stopProgress, []);

  // Stage a chosen/dropped file (validate type/size) without processing it yet.
  const stageFile = (file: File) => {
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      alert('Only PDF files are accepted.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      alert('File exceeds the 25 MB limit.');
      return;
    }
    setSelectedFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Submit the staged PDF: the backend renders every page, reads it with the
  // DeepSeek vision model, chunks the text and writes notes.
  const handleGenerate = async () => {
    const file = selectedFile;
    if (!file) return;
    setIsProcessing(true);
    startProgress();

    const form = new FormData();
    form.append('file', file);

    try {
      const res = await apiRequest<{
        notes: string;
        notes_html: string;
        pages_read?: number;
        total_pages?: number;
        chunks?: number;
        truncated?: boolean;
      }>(API_ENDPOINTS.NOTES_GENERATE, {
        method: 'POST',
        body: form,
        timeout: 20 * 60 * 1000,
      });

      const raw = res.data?.notes_html || res.data?.notes || '';
      const html = renderRichContent(raw);
      setGeneratedHtml(html || '<p>No notes were generated. Please try again.</p>');
      setResultMeta({
        pages: res.data?.pages_read,
        totalPages: res.data?.total_pages,
        chunks: res.data?.chunks,
        truncated: res.data?.truncated,
      });
      setProgress(100);
      setCurrentView('result');
    } catch (err) {
      console.error('Notes generation failed', err);
      const serverMsg = err instanceof Error ? err.message : '';
      alert(
        (serverMsg && serverMsg !== 'API request failed' ? `${serverMsg}\n\n` : '') +
          'Could not generate notes from that PDF. Please make sure it is a valid PDF and try again.'
      );
    } finally {
      stopProgress();
      setIsProcessing(false);
      setStatusMessage('');
      setIsDragging(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) stageFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && !isProcessing) stageFile(file);
  };

  const exportTxt = () => {
    downloadText('notes.txt', richContentToPlainText(generatedHtml));
  };

  const exportDocx = async () => {
    try {
      const { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType } = await import('docx');
      const fileSaverModule = await import('file-saver');
      const saveAs = fileSaverModule.default || fileSaverModule.saveAs;

      const convertHtmlToDocxElements = (html: string) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const elements: any[] = [];

        Array.from(doc.body.childNodes).forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            switch (element.tagName.toLowerCase()) {
              case 'h1':
                elements.push(new Paragraph({ text: element.textContent || '', heading: HeadingLevel.HEADING_1 }));
                break;
              case 'h2':
                elements.push(new Paragraph({ text: element.textContent || '', heading: HeadingLevel.HEADING_2 }));
                break;
              case 'h3':
                elements.push(new Paragraph({ text: element.textContent || '', heading: HeadingLevel.HEADING_3 }));
                break;
              case 'p':
                elements.push(new Paragraph({ children: [new TextRun(element.textContent || '')] }));
                break;
              case 'table': {
                const tableRows: any[] = [];
                element.querySelectorAll('tr').forEach((row) => {
                  const tableCells: any[] = [];
                  row.querySelectorAll('td, th').forEach((cell) => {
                    tableCells.push(new TableCell({
                      children: [new Paragraph({ children: [new TextRun(cell.textContent || '')] })],
                    }));
                  });
                  tableRows.push(new TableRow({ children: tableCells }));
                });
                elements.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: tableRows }));
                break;
              }
              case 'ul':
              case 'ol':
                element.querySelectorAll('li').forEach((item) => {
                  elements.push(new Paragraph({ children: [new TextRun('• ' + (item.textContent || ''))] }));
                });
                break;
              default:
                if (element.textContent?.trim()) {
                  elements.push(new Paragraph({ children: [new TextRun(element.textContent)] }));
                }
            }
          } else if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
            elements.push(new Paragraph({ children: [new TextRun(node.textContent)] }));
          }
        });
        return elements;
      };

      const doc = new Document({ sections: [{ properties: {}, children: convertHtmlToDocxElements(generatedHtml) }] });
      const blob = await Packer.toBlob(doc);
      saveAs(blob, 'notes.docx');
    } catch (err) {
      console.error('DOCX export failed', err);
      alert('Failed to export Word document. Please try again. Error: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const reset = () => {
    setSelectedFile(null);
    setGeneratedHtml('');
    setResultMeta({});
    setCurrentView('input');
  };

  return (
    <div className="module-container writing-module">
      <div className="module-header">
        <span className="module-icon">📝</span>
        <h2 className="module-title">{t('moduleHeaders.notes.title')}</h2>
        <p className="module-subtitle">{t('moduleHeaders.notes.subtitle')}</p>
      </div>

      <ModuleTopExtras moduleId="notes" />

      <div className="module-content">
        {currentView === 'input' && !isProcessing && (
          <div className="content-section">
            <div className="section-header">
              <h3>Upload your PDF</h3>
              <p>Every page is read by AI, then turned into clear study notes. (PDF only, max 25 MB)</p>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="application/pdf,.pdf"
              onChange={handleFileChange}
            />

            <div
              className={`file-upload${isDragging ? ' dragover' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <span className="file-upload-icon">📄</span>
              <div className="file-upload-text">Drag &amp; drop your PDF here</div>
              <div className="file-upload-hint">
                {selectedFile ? selectedFile.name : 'or click to browse — PDF files only'}
              </div>
              <span className="file-upload-button">{selectedFile ? 'Replace PDF' : 'Choose PDF'}</span>
            </div>

            {selectedFile && (
              <div className="generation-actions" style={{ flexDirection: 'column', gap: '12px' }}>
                <button className="btn btn-primary" onClick={handleGenerate}>
                  Generate Notes
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                >
                  Remove file
                </button>
              </div>
            )}
          </div>
        )}

        {isProcessing && (
          <div className="content-section">
            <div className="section-header">
              <h3>Generating Notes</h3>
              {selectedFile && <p className="file-name" style={{ marginBottom: 0 }}>{selectedFile.name}</p>}
            </div>
            <AdProcessingBanner active={isProcessing} />
            <div className="progress-container">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${Math.round(progress)}%` }}></div>
              </div>
              <div className="progress-message">
                {statusMessage} {Math.round(progress)}%
              </div>
            </div>
          </div>
        )}

        {currentView === 'result' && (
          <div className="content-section">
            <div className="section-header">
              <h3>Your Generated Notes</h3>
              {selectedFile && <p className="file-name" style={{ marginBottom: 0 }}>{selectedFile.name}</p>}
              {typeof resultMeta.pages === 'number' && (
                <p style={{ fontSize: '14px', color: 'var(--text-light)', margin: '4px 0 0' }}>
                  Read {resultMeta.pages} of {resultMeta.totalPages ?? resultMeta.pages} page
                  {(resultMeta.totalPages ?? resultMeta.pages) !== 1 ? 's' : ''}
                  {typeof resultMeta.chunks === 'number' && resultMeta.chunks > 1 ? ` · ${resultMeta.chunks} sections` : ''}
                  {resultMeta.truncated ? ' · only the first pages were processed' : ''}
                </p>
              )}
            </div>
            <div className="notes-print-area">
              <div className="print-only notes-print-header">
                <h1>Study Notes</h1>
                {selectedFile && <p>{selectedFile.name}</p>}
              </div>
              <div className="results-container rich-content" dangerouslySetInnerHTML={{ __html: generatedHtml }} />
            </div>
            <div className="result-actions">
              <button className="btn btn-secondary" onClick={() => window.print()}>🖨 Print / Save PDF</button>
              <button className="btn btn-secondary" onClick={exportDocx}>Export Word</button>
              <button className="btn btn-secondary" onClick={exportTxt}>Export TXT</button>
              <button className="btn btn-primary" onClick={reset}>Create New Notes</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesModule;
